import {
  setCookie,
  deleteCookieAllDomains,
  getSharedCookieDomain,
} from "../Core/utils/cookieUtils";

const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;

/**
 * Lifetime of the auth cookies handed to a sibling app (Scratch) for a
 * NON-STUDENT session. The navigation is immediate and the receiver deletes the
 * pair once it has adopted it, so this only bounds the window in which a staff
 * token could linger on the shared domain if that navigation never completes.
 */
export const STAFF_HANDOFF_TTL_SECONDS = 120;

/**
 * Writes the shared cy-sso `Authorization` + `refresh-token` cookies (used by
 * the cross-app auto-login / sync). Single chokepoint for auth-cookie writes,
 * so it also bumps the `cy_auth_sync` flag — the signal both apps watch (on
 * boot and on tab focus) to re-read the shared cookies, e.g. after a profile
 * switch. "whenever the cookies change → bump the flag."
 *
 * @param {{ACCESS_TOKEN:string, REFRESH_TOKEN:string}} auth
 * @param {number} [login=0] When truthy, always (re)write even if cookies exist.
 * @param {object} [opts]
 * @param {number} [opts.maxAge=ONE_YEAR_SECONDS] Cookie lifetime in seconds.
 *   Pass a short TTL for a NON-STUDENT handoff (e.g. opening Scratch as a
 *   mentor): staff sessions are app-local, so their tokens must not sit on the
 *   browser-wide shared domain if the receiving app never loads to consume
 *   them. Receivers still delete the pair after consuming it — belt and braces.
 */
const setAuthCookies = (auth, login = 0, { maxAge = ONE_YEAR_SECONDS } = {}) => {
  const access = getCookieValue("Authorization");
  const refresh = getCookieValue("refresh-token");
  if (login || !access || !refresh) {
    const domain = getSharedCookieDomain();
    // Clear any stale same-named cookies on OTHER domains first, so a cookie
    // left by an older build on a different domain (e.g. `.codeyoung.com` vs
    // `.dev.codeyoung.com`) can't linger and win in `getCookie` (cross-domain
    // duplicates order unpredictably in document.cookie). After this there is
    // exactly one auth cookie, on the canonical shared domain.
    deleteCookieAllDomains("Authorization");
    deleteCookieAllDomains("refresh-token");
    const opts = { domain, maxAge, sameSite: "Lax" };
    setCookie("Authorization", auth.ACCESS_TOKEN, opts);
    setCookie("refresh-token", auth.REFRESH_TOKEN, opts);
    setCookie("cy_auth_sync", String(Date.now()), opts);
  }
};

// Local read helper (avoids pulling js-cookie just to read two values).
function getCookieValue(name) {
  const target = `${name}=`;
  const cookies = document.cookie ? document.cookie.split("; ") : [];
  for (const c of cookies) {
    if (c.startsWith(target)) return c.slice(target.length);
  }
  return null;
}

export default setAuthCookies;
